import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import type {
  PulseHeartbeatResponse,
  PulseOutcomeKind,
  PulseOutcomeRequest,
  PulseOutcomeResponse,
  PulseSuggestion,
} from './types';

export const PULSE_HEARTBEAT_INTERVAL_MS = 60_000;

export interface PulseTransport {
  heartbeat: (signal: AbortSignal) => Promise<PulseHeartbeatResponse>;
  outcome: (
    pulseId: string,
    request: PulseOutcomeRequest,
    signal: AbortSignal,
  ) => Promise<PulseOutcomeResponse>;
}

export interface PulseControllerOptions {
  transport: PulseTransport;
  /** Both rollout gates, desktop width, authentication, and launcher visibility. */
  enabled: boolean;
  /** An opaque local key combining the authenticated user and organization. Never sent. */
  identityKey: string;
  suppressed: boolean;
  /** Live parent guard distinguishes navigation unmount from sign-out/account replacement. */
  isIdentityCurrent?: () => boolean;
  onOpenReader: (path: string) => void;
  onInteraction?: (action: 'guide_clicked' | 'dismiss_clicked') => void;
}

/** Reader paths contain two slug segments and must remain inside the local product guide. */
export const isPulseReaderPath = (path: string): boolean =>
  /^\/kb\/[a-zA-Z0-9_-]+\/[a-zA-Z0-9_-]+$/.test(path);

/** No targeting: accept only a live, navigable server suggestion. */
function canDisplay(pulse: PulseSuggestion): boolean {
  const expiry = Date.parse(pulse.expires_at);
  return (
    Boolean(pulse.prompt.trim()) &&
    Number.isFinite(expiry) &&
    expiry > Date.now() &&
    isPulseReaderPath(pulse.reader_url)
  );
}

export function usePulseController(options: PulseControllerOptions) {
  const latest = useRef(options);
  latest.current = options;
  const [documentVisible, setDocumentVisible] = useState(
    () => document.visibilityState === 'visible',
  );
  const [suggestion, setSuggestion] = useState<PulseSuggestion | null>(null);
  const current = useRef<PulseSuggestion | null>(null);
  const lastInput = useRef(-Infinity);
  const lastHeartbeat = useRef(-Infinity);
  const heartbeat = useRef<AbortController | null>(null);
  const outstandingOutcomes = useRef(new Set<AbortController>());
  const seen = useRef(new Set<string>());
  const reported = useRef(new Set<string>());
  const identity = useRef(options.identityKey);
  const epoch = useRef(0);
  const enabled = options.enabled && !options.suppressed && documentVisible;
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  const hide = useCallback(() => {
    current.current = null;
    setSuggestion(null);
  }, []);

  // Suggestions belong to the user and organization, so ordinary navigation preserves them.
  // Invalidate before paint when that identity or display eligibility changes.
  useLayoutEffect(() => {
    const outcomes = outstandingOutcomes.current;
    epoch.current += 1;
    heartbeat.current?.abort();
    heartbeat.current = null;
    hide();
    if (identity.current !== options.identityKey) {
      outcomes.forEach(request => request.abort());
      outcomes.clear();
      identity.current = options.identityKey;
      seen.current.clear();
      reported.current.clear();
      lastInput.current = -Infinity;
      lastHeartbeat.current = -Infinity;
    }
    return () => {
      epoch.current += 1;
      heartbeat.current?.abort();
      heartbeat.current = null;
      // A guide click can unmount this entire surface. Its outcome still belongs to this user.
      if (latest.current.isIdentityCurrent?.() === false) {
        outcomes.forEach(request => request.abort());
        outcomes.clear();
      }
    };
  }, [options.identityKey, enabled, hide]);

  useEffect(() => {
    const visibility = () => {
      const visible = document.visibilityState === 'visible';
      if (!visible) {
        epoch.current += 1;
        heartbeat.current?.abort();
        heartbeat.current = null;
        hide();
      }
      setDocumentVisible(visible);
    };
    document.addEventListener('visibilitychange', visibility);
    return () => document.removeEventListener('visibilitychange', visibility);
  }, [hide]);

  useEffect(() => {
    if (!enabled) return;
    let active = true;
    const tick = async () => {
      const now = Date.now();
      if (
        !active ||
        !enabledRef.current ||
        document.visibilityState !== 'visible' ||
        latest.current.isIdentityCurrent?.() === false ||
        now - lastInput.current >= PULSE_HEARTBEAT_INTERVAL_MS ||
        now - lastHeartbeat.current < PULSE_HEARTBEAT_INTERVAL_MS ||
        heartbeat.current ||
        current.current
      )
        return;
      const request = new AbortController();
      const requestEpoch = epoch.current;
      heartbeat.current = request;
      lastHeartbeat.current = now;
      try {
        const response = await latest.current.transport.heartbeat(request.signal);
        if (
          !active ||
          request.signal.aborted ||
          requestEpoch !== epoch.current ||
          !enabledRef.current ||
          document.visibilityState !== 'visible' ||
          Date.now() - lastInput.current >= PULSE_HEARTBEAT_INTERVAL_MS ||
          latest.current.isIdentityCurrent?.() === false
        )
          return;
        if (
          response.kind === 'suggestion' &&
          canDisplay(response) &&
          !seen.current.has(response.pulse_id)
        ) {
          seen.current.add(response.pulse_id);
          current.current = response;
          setSuggestion(response);
        }
      } catch {
        // The next eligible minute is the retry. No interruption or toast for a passive suggestion.
      } finally {
        if (heartbeat.current === request) heartbeat.current = null;
      }
    };
    const input = (event: Event) => {
      if (document.visibilityState !== 'visible') return;
      lastInput.current = Date.now();
      const target = event.target instanceof Element ? event.target : null;
      if (target?.closest('[data-jot-pulse], [data-jot-launcher]')) return;
      // Page activity keeps polling eligible; it never retires a visible suggestion.
      void tick();
    };
    const events = [
      'pointerdown',
      'pointermove',
      'keydown',
      'input',
      'wheel',
      'touchstart',
    ] as const;
    events.forEach(event => document.addEventListener(event, input, { passive: true }));
    const timer = window.setInterval(() => void tick(), PULSE_HEARTBEAT_INTERVAL_MS);
    return () => {
      active = false;
      window.clearInterval(timer);
      events.forEach(event => document.removeEventListener(event, input));
    };
  }, [enabled, options.identityKey]);

  useEffect(() => {
    if (!suggestion) return;
    const timer = window.setTimeout(
      hide,
      Math.max(0, Date.parse(suggestion.expires_at) - Date.now()),
    );
    return () => window.clearTimeout(timer);
  }, [suggestion, hide]);

  const report = useCallback((pulse: PulseSuggestion, outcome: PulseOutcomeKind) => {
    if (latest.current.isIdentityCurrent?.() === false) return;
    const key = `${pulse.pulse_id}:${outcome}`;
    if (reported.current.has(key)) return;
    reported.current.add(key);
    const request = new AbortController();
    outstandingOutcomes.current.add(request);
    // Start before reader navigation. Completion never updates component state.
    try {
      void latest.current.transport
        .outcome(pulse.pulse_id, { outcome }, request.signal)
        .catch(() => undefined)
        .finally(() => outstandingOutcomes.current.delete(request));
    } catch {
      outstandingOutcomes.current.delete(request);
    }
  }, []);

  const usable = useCallback(() => {
    const pulse = current.current;
    return enabledRef.current &&
      document.visibilityState === 'visible' &&
      latest.current.isIdentityCurrent?.() !== false &&
      pulse &&
      canDisplay(pulse)
      ? pulse
      : null;
  }, []);
  const shown = useCallback(() => {
    const pulse = usable();
    if (pulse) report(pulse, 'shown');
  }, [report, usable]);
  const openGuide = useCallback(() => {
    const pulse = usable();
    if (!pulse) return;
    hide();
    report(pulse, 'clicked');
    latest.current.onInteraction?.('guide_clicked');
    latest.current.onOpenReader(pulse.reader_url);
  }, [hide, report, usable]);
  const dismiss = useCallback(() => {
    const pulse = usable();
    if (!pulse?.dismissible) return;
    hide();
    report(pulse, 'dismissed');
    latest.current.onInteraction?.('dismiss_clicked');
  }, [hide, report, usable]);

  return {
    suggestion:
      enabled && identity.current === options.identityKey && suggestion && canDisplay(suggestion)
        ? suggestion
        : null,
    shown,
    openGuide,
    dismiss,
    hide,
  };
}
